import React, { Component } from 'react';
import { Route } from 'react-router-dom';

// Load an example payload
import { userRolePermissions } from '../../../helpers/userRolePermissionList';

/**
 * Permission gating for the Davinci router.
 *
 * Every privileged route is wrapped in {@link HasRolePermission}, which checks
 * the `scopes` array on the signed-in user (persisted as `shUser` in
 * `localStorage`) against the {@link PERMISSIONS} catalogue. When the scope is
 * missing the children render `null`, so the route silently disappears. See
 * `docs/routing-and-permissions.md`.
 *
 * @module app/modules/auth/components/RolesRouting
 */

/** Props for {@link HasRolePermission}: one scope, or a list of acceptable scopes. */
interface RequireType {
    required_permissions: string[] | string;
}

/**
 * Catalogue of Davinci permission scopes, grouped by feature. Each feature has
 * a `read` and `write` scope of the form `davinci.<feature>.<action>`. Route
 * guards reference these instead of hard-coding scope strings.
 */
const PERMISSIONS = {
    eanlist: {
        read: 'davinci.eanlist.read',
        write: 'davinci.eanlist.write',
    },
    virtualproducts: {
        read: 'davinci.virtualproducts.read',
        write: 'davinci.virtualproducts.write',
    },
    attributerelations: {
        read: 'davinci.attributerelations.read',
        write: 'davinci.attributerelations.write',
    },
    rules: {
        read: 'davinci.rules.read',
        write: 'davinci.rules.write',
    },
    colorlabbulkdesigner: {
        read: 'davinci.colorlabbulkdesigner.read',
        write: 'davinci.colorlabbulkdesigner.write',
    },
    testorders: {
        read: 'davinci.testorders.read',
        write: 'davinci.testorders.write',
    },
    assistant: {
        read: 'davinci.assistant.read',
        write: 'davinci.assistant.write',
    },
}

/**
 * Render children only when the signed-in user holds the required scope(s).
 *
 * Reads `shUser.scopes` from `localStorage`. When `required_permissions` is an
 * array, holding *any one* of the scopes grants access (OR semantics). Renders
 * nothing when the user is absent or unscoped.
 *
 * @param props.required_permissions  A single scope or a list of acceptable scopes.
 * @param props.children  The protected element to reveal when authorised.
 */
const HasRolePermission: React.FC<RequireType> = ({required_permissions, children}) => {
    const requiredRoles: string[] | string = required_permissions;

    const grantPermission = () => {
        let foundRole = false;

        if(localStorage.getItem('shUser') === null){
            return foundRole;
        }

        const permittedRoles = JSON.parse(localStorage.getItem('shUser') ?? '').scopes;

        if(Array.isArray(requiredRoles)) {
            requiredRoles.forEach((role) => {
                if(permittedRoles.includes(role)) {
                    foundRole = true;
                }
            })
        } else {
            if (permittedRoles.includes(requiredRoles)) {
                foundRole = true;
            }
        }

        return foundRole;
    }

    return (
        <>
            {grantPermission() ? children : null}
        </>
    );
}

export { HasRolePermission, PERMISSIONS }
